import { useEffect, useState } from "react"
import classNames from "classnames"
import { useAppSelector } from "@/store/hooks"
import { getUserCreds, socket } from "@/utils/utils"
import { allPieces } from "@/utils/data"
import { FAST_PACE_VOTE_TIME } from "@mono/constants"

/**
 * The fast-pace proposal on the table, drawn at the head of the left player
 * column — in the slot the "make game fast pace" button occupies the rest of
 * the time (see FastPaceControl, which stands down while a vote is open).
 *
 * Not a toast: it is driven entirely by `situation.fastPaceVote`, so it appears
 * when the vote opens, updates as answers come in and disappears the moment the
 * server closes it — including for a player who reloads halfway through. What
 * replaces it is an ordinary toast carrying the result (see ToastContainer).
 *
 * It is sized by the slot it sits in (SidebarLeft), which gives it exactly the
 * player cards' width so it reads as the top of that column rather than as
 * something floating over it. The room it grows into is real, not borrowed:
 * PlayersList widens the empty strip it keeps above the topmost card while a
 * vote is open, on *both* sides at once so the two columns stay row-aligned,
 * and the extra comes out of the cards' height for the 45 seconds the vote
 * lasts. So nothing here can land on a player card — but the budget is still
 * finite, and this must stay around 10rem tall (see SidebarLeft's slot for the
 * arithmetic). Everything is in rem so that holds at every width: the root
 * font-size shrinks with the viewport, and the sidebar shrinks with it.
 *
 * Below 1000px (`mobile:`) that rem scaling is the problem rather than the
 * answer: index.css halves the root font-size there, so `text-sm` lands at 7px
 * and the whole panel reads as a smudge. So every size is restated in raw px
 * for that width — the repo's usual answer (see ToastContainer and
 * FastPaceVoteStartedNotification) — which puts the type back at ~11px. That
 * makes the panel ~112px tall instead of the ~78px the rem version collapsed
 * to, more than the strip above the topmost card holds. It overlaps that card
 * rather than pushing it down: PlayersList keeps its strip at the closed height
 * on a phone on purpose, because a column that short cannot spare the room and
 * the whole list lurching mid-turn reads worse than the overlap.
 *
 * It shows the tally rather than just "waiting…": a player deciding whether to
 * accept wants to know that others already have, and a player who has already
 * answered is owed some sign that the vote is still alive. The seat dots say
 * who is still out — a vote stuck on one person reads very differently from a
 * vote stuck on four.
 *
 * The countdown is worked out from `proposedAt` on the client, the same way a
 * pending trade offer's is. It is a display of the server's deadline, not the
 * deadline itself — the server closes the vote on its own clock regardless of
 * what this says.
 */
const FastPaceVotePanel: React.FC = () => {
  const gameId = useAppSelector((state) => state.global.myGame?.id)
  const vote = useAppSelector(
    (state) => state.global.myGame?.situation?.fastPaceVote
  )
  const players = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const users = useAppSelector((state) => state.global.myGame?.players)

  // Ticks the countdown. Runs only while a vote is open.
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!vote) return
    setNow(Date.now())
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [vote?.proposedAt])

  if (!vote) return null

  const myOrder = players?.find(
    (player) => player.id === getUserCreds().email
  )?.order
  const myVote = myOrder === undefined ? undefined : vote.votes[myOrder]
  const canVote =
    myOrder !== undefined &&
    vote.voterOrders.includes(myOrder) &&
    myVote === undefined

  const answers = vote.voterOrders.map((order) => vote.votes[order])
  const acceptedCount = answers.filter((answer) => answer === true).length
  const rejectedCount = answers.filter((answer) => answer === false).length
  const votedCount = acceptedCount + rejectedCount
  const pendingCount = vote.voterOrders.length - votedCount

  const secondsLeft = Math.max(
    0,
    Math.ceil((vote.proposedAt + FAST_PACE_VOTE_TIME * 1000 - now) / 1000)
  )
  const isUrgent = secondsLeft <= 10

  const proposer = players?.find(
    (player) => player.order === vote.proposedByOrder
  )
  const proposerName =
    proposer?.order === myOrder
      ? "You"
      : users?.find((user) => user.email === proposer?.id)?.username ||
        "A player"

  const castVote = (accepted: boolean) => {
    socket.emit("game.fast-pace-vote", { roomid: gameId || "", accepted })
  }

  return (
    <div
      data-testid="notification.fast-pace-vote"
      // Same skin as the fast-pace badge next door (FastPaceControl): this is
      // that corner's control mid-decision, not a notification that drifted in.
      // Width comes from the slot, which is cut to the player cards'.
      className="fast-pace-vote-panel relative w-full overflow-hidden rounded-xl border border-amber-300/50 bg-gradient-to-b from-[#4a3308]/95 to-[#150f04]/95 pb-3 pl-4 pr-3 pt-2.5 font-poppins text-white shadow-[0_4px_0_0_#78350f,0_10px_22px_-6px_rgba(0,0,0,0.6)] backdrop-blur-sm mobile:rounded-[10px] mobile:pb-[7px] mobile:pl-[12px] mobile:pr-[9px] mobile:pt-[7px]"
    >
      {/* Accent rail down the left edge, same as the other notifications. */}
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-amber-300 to-amber-500 mobile:w-[5px]" />

      {/* Row one is the headline and the clock — what this is and how long is
          left, the two things worth reading from the corner of an eye. */}
      <div className="flex items-center gap-2.5 mobile:gap-[7px]">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/20 ring-1 ring-amber-300/40 mobile:h-[20px] mobile:w-[20px]">
          <i className="fa-solid fa-bolt text-amber-300 text-sm mobile:text-[10px]" />
        </span>
        <span className="min-w-0 truncate text-sm font-bold uppercase tracking-[0.12em] text-amber-200 mobile:text-[11px] mobile:tracking-[0.06em]">
          Fast pace vote
        </span>
        {/* Fixed width so the row doesn't shuffle each tick. Turning red is
            enough to pull the eye at the end; the panel itself doesn't change
            what it is doing, since the game is still being played around it. */}
        <span
          data-testid="fast-pace-vote.seconds-left"
          className={classNames(
            "ml-auto w-12 shrink-0 text-right text-xl font-bold tabular-nums leading-none mobile:w-[28px] mobile:text-[16px]",
            isUrgent ? "text-red-300" : "text-white/80"
          )}
        >
          {secondsLeft}s
        </span>
      </div>

      {/* What is actually being asked. The detail (shorter timers, quicker
          animations, everyone has to agree) lives on the tooltip here and in
          the result toast. */}
      <div
        className="mt-2 truncate text-sm leading-snug text-white/70 mobile:mt-[4px] mobile:text-[11px]"
        title={`${proposerName} asked everyone to switch to fast pace — shorter turn timers and quicker animations for the rest of the game. Everyone has to agree.`}
      >
        <span className="font-semibold text-amber-100">{proposerName}</span>{" "}
        want
        {proposerName === "You" ? "" : "s"} to speed the game up
      </div>

      {/* One dot per seat with a say, so it is clear who the vote is waiting on
          rather than just how many. */}
      <div className="mt-2.5 flex items-center gap-1.5 mobile:mt-[5px] mobile:gap-[4px]">
        {vote.voterOrders.map((order) => {
          const player = players?.find((p) => p.order === order)
          const piece = allPieces.find((pc) => pc.id === player?.pieceId)
          const answer = vote.votes[order]
          const name =
            users?.find((user) => user.email === player?.id)?.username ||
            "Player"
          return (
            <div
              key={order}
              title={
                answer === undefined
                  ? `${name} — no answer yet`
                  : `${name} ${answer ? "accepted" : "rejected"}`
              }
              className={classNames(
                "relative flex h-6 w-6 items-center justify-center rounded-full bg-black/50 ring-1 mobile:h-[17px] mobile:w-[17px]",
                answer === true
                  ? "ring-emerald-400/80"
                  : answer === false
                  ? "ring-red-500/80"
                  : "ring-white/25 opacity-50"
              )}
            >
              {piece?.imgSrc ? (
                <img
                  className="h-4 w-4 object-contain mobile:h-[11px] mobile:w-[11px]"
                  src={piece.imgSrc}
                />
              ) : (
                <i className="fa-solid fa-user text-white/40 text-[0.6rem] mobile:text-[8px]" />
              )}
              {answer !== undefined && (
                <span
                  className={classNames(
                    "absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full ring-1 ring-[#150f04] mobile:h-[9px] mobile:w-[9px]",
                    answer ? "bg-emerald-500" : "bg-red-500"
                  )}
                >
                  <i
                    className={classNames(
                      "fa-solid text-[0.45rem] text-white mobile:text-[6px]",
                      answer ? "fa-check" : "fa-xmark"
                    )}
                  />
                </span>
              )}
            </div>
          )
        })}

        {/* The counts the dots already spell out one by one; this is only the
            progress, for a glance that doesn't stop to read them. */}
        <span
          data-testid="fast-pace-vote.voted-count"
          title={`${acceptedCount} accepted, ${rejectedCount} rejected, ${pendingCount} still to answer`}
          className="ml-auto text-xs font-semibold tabular-nums text-white/50 mobile:text-[10px]"
        >
          {votedCount}/{vote.voterOrders.length} voted
        </span>
      </div>

      {canVote ? (
        // Sized like the HUD buttons rather than like a toast's: this is the
        // one thing in the corner that has to be hit, often mid-turn.
        <div className="mt-2.5 flex gap-2 mobile:mt-[5px] mobile:gap-[6px]">
          <button
            onClick={() => castVote(true)}
            data-testid="fast-pace-vote.accept-btn"
            className="flex-1 rounded-lg border border-emerald-300/50 bg-gradient-to-b from-emerald-400 to-emerald-600 py-2.5 text-sm font-bold uppercase tracking-wide leading-none text-white shadow-[0_3px_0_0_#065f46,inset_0_1px_0_0_rgba(255,255,255,0.25)] transition-[transform,filter] duration-150 hover:brightness-110 active:translate-y-[3px] active:shadow-none mobile:rounded-[8px] mobile:py-[7px] mobile:text-[11px]"
          >
            <i className="fa-solid fa-check mr-1.5 mobile:mr-[4px]" />
            Accept
          </button>
          <button
            onClick={() => castVote(false)}
            data-testid="fast-pace-vote.reject-btn"
            className="flex-1 rounded-lg border border-red-300/50 bg-gradient-to-b from-red-400 to-red-600 py-2.5 text-sm font-bold uppercase tracking-wide leading-none text-white shadow-[0_3px_0_0_#7f1d1d,inset_0_1px_0_0_rgba(255,255,255,0.25)] transition-[transform,filter] duration-150 hover:brightness-110 active:translate-y-[3px] active:shadow-none mobile:rounded-[8px] mobile:py-[7px] mobile:text-[11px]"
          >
            <i className="fa-solid fa-xmark mr-1.5 mobile:mr-[4px]" />
            Reject
          </button>
        </div>
      ) : (
        // No answer counts as a no — said here only when there is still somebody
        // to wait on, since that is the only time it changes what you'd expect.
        // Kept to the height of the button row so the panel doesn't resize under
        // the player column the moment you answer.
        <div className="mt-2.5 flex h-10 items-center gap-2 text-xs leading-none text-white/50 mobile:mt-[5px] mobile:h-[25px] mobile:gap-[6px] mobile:text-[10px]">
          {myVote !== undefined && (
            <span
              className={classNames(
                "inline-flex shrink-0 items-center gap-1 font-semibold",
                myVote ? "text-emerald-300" : "text-red-300"
              )}
            >
              <i
                className={classNames(
                  "fa-solid",
                  myVote ? "fa-check" : "fa-xmark"
                )}
              />
              You {myVote ? "accepted" : "rejected"}
            </span>
          )}
          <span className="min-w-0 truncate">
            {pendingCount > 0
              ? `Waiting on ${pendingCount} — no answer counts as a no`
              : "Counting the votes…"}
          </span>
        </div>
      )}

      {/* The deadline as a bar, draining along the foot of the panel. The
          countdown above is the exact number; this is the part you can read
          without looking straight at it. Driven off the same seconds as the
          digits (rather than a fixed-duration CSS animation) so a player who
          reloads mid-vote gets a bar at the right point, and the one-second
          linear transition is what makes it slide rather than step. */}
      <span
        aria-hidden
        className={classNames(
          "absolute bottom-0 left-0 h-1 w-full origin-left bg-gradient-to-r transition-transform duration-1000 ease-linear mobile:h-[4px]",
          isUrgent ? "from-red-400 to-red-600" : "from-amber-300 to-amber-500"
        )}
        style={{ transform: `scaleX(${secondsLeft / FAST_PACE_VOTE_TIME})` }}
      />
    </div>
  )
}

export default FastPaceVotePanel
